import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DRAWER_HEIGHT, DRAWER_OVERFLOW } from "@constants/Drawer";
import { useDrawerLimits } from "@hooks/useDrawerLimits/useDrawerLimits";
import { DragState, UseDrawerOptions } from "@models/shared/shared.model";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";
import { clamp } from "@utils/common.utils";
import { findNearestSnapPoint } from "@utils/drawerHelpers.utils";

export function useDrawer({
  isOpen,
  onClose,
  useSnapPoints = true,
}: UseDrawerOptions) {
  const [isDragging, setIsDragging] = useState(false);

  const dragStateRef = useRef<DragState | null>(null);
  const drawerContentRef = useRef<HTMLElement | null>(null);

  const dispatch = useDispatch();

  const { maxHeight, minHeight } = useDrawerLimits();

  const openPercent = useSelector(
    (state: RootState) => state.drawer.openPercent
  );

  useEffect(() => {
    if (openPercent > DRAWER_HEIGHT.TWENTY_FIVE_PERCENT) {
      document.body.style.overflow = DRAWER_OVERFLOW.HIDDEN;
    } else {
      document.body.style.overflow = DRAWER_OVERFLOW.AUTO;
    }
  }, [openPercent]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = DRAWER_OVERFLOW.AUTO;
    };
  }, []);

  useEffect(() => {
    dispatch(
      drawerSlice.actions.setOpenPercent(
        isOpen ? maxHeight : DRAWER_HEIGHT.MIN_OPEN_PERCENT
      )
    );
  }, [isOpen, dispatch]);

  const startDrag = useCallback(
    (clientY: number) => {
      dragStateRef.current = {
        dragging: true,
        startPercent: openPercent,
        startY: clientY,
      };
      setIsDragging(true);
    },
    [openPercent]
  );

  const handlePointerMove = useCallback(
    (clientY: number) => {
      const state = dragStateRef.current;
      if (!state?.dragging) return;

      const deltaY = state.startY - clientY;
      const vh = window.innerHeight || 1;
      const deltaPercent = (deltaY / vh) * DRAWER_HEIGHT.MAX_OPEN_PERCENT;

      dispatch(
        drawerSlice.actions.setOpenPercent(
          clamp(
            state.startPercent + deltaPercent,
            DRAWER_HEIGHT.MIN_OPEN_PERCENT,
            DRAWER_HEIGHT.MAX_OPEN_PERCENT
          )
        )
      );
    },
    [dispatch]
  );

  const snappedOpenPercent = useMemo(() => {
    if (!useSnapPoints) return openPercent;
    return findNearestSnapPoint(openPercent, minHeight, maxHeight);
  }, [openPercent, useSnapPoints, maxHeight, minHeight]);

  const endDrag = useCallback(() => {
    const state = dragStateRef.current;
    if (!state?.dragging) return;

    state.dragging = false;
    setIsDragging(false);

    if (snappedOpenPercent === DRAWER_HEIGHT.MIN_OPEN_PERCENT) {
      onClose();
    } else {
      dispatch(drawerSlice.actions.setOpenPercent(snappedOpenPercent));
    }
  }, [dispatch, onClose, snappedOpenPercent]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) =>
      handlePointerMove(event.clientY);

    const handleMouseUp = () => endDrag();

    const handleTouchMove = (event: TouchEvent) =>
      handlePointerMove(event.touches[0].clientY);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleMouseUp);
    window.addEventListener("touchcancel", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
      window.removeEventListener("touchcancel", handleMouseUp);
    };
  }, [isDragging, handlePointerMove, endDrag]);

  return {
    drawerContentRef,
    isDragging,
    onClose,
    openPercent,
    startDrag,
  };
}
